// contexts/SheetContext.js - Version corrigée pour la gestion des onglets cachés

import React, { createContext, useState, useEffect, useContext } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AuthContext } from './AuthContext';
import { config } from '../config';
import sheetService from '../services/sheetService';
import AsyncStorageHelper from '../services/asyncStorageHelper';

export const SheetContext = createContext();

export const SheetProvider = ({ children }) => {
  const { user, getValidAccessToken, isUserAdmin } = useContext(AuthContext);
  const [competitions, setCompetitions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(0);
  
  // 🆕 État pour l'affichage des onglets cachés
  const [showHiddenSheets, setShowHiddenSheets] = useState(false);
  const [hiddenSheetsCount, setHiddenSheetsCount] = useState(0);

  // 🆕 Charger la préférence d'affichage des onglets cachés
  useEffect(() => {
    const loadShowHiddenPreference = async () => {
      try {
        // Seulement pour les admins
        if (isUserAdmin()) {
          const showHidden = await AsyncStorageHelper.getItem('showHiddenSheets', false);
          setShowHiddenSheets(showHidden === true);
          console.log('🔧 Loaded showHiddenSheets preference:', showHidden);
        }
      } catch (e) {
        console.error('Failed to load show hidden sheets preference:', e);
      }
    };

    loadShowHiddenPreference();
  }, [isUserAdmin]);

  useEffect(() => {
    const loadRefreshInterval = async () => {
      try {
        const interval = await AsyncStorage.getItem('refreshInterval');
        if (interval) {
          setRefreshInterval(parseInt(interval, 10));
        }
      } catch (e) {
        console.error('Failed to load refresh interval:', e);
      }
    };

    loadRefreshInterval();
  }, []);

  useEffect(() => {
    if (user && getValidAccessToken) {
      fetchCompetitions();
    }
  }, [user, getValidAccessToken, showHiddenSheets]); // 🆕 Ajouter showHiddenSheets comme dépendance

  const fetchCompetitions = async () => {
    if (!user || !getValidAccessToken) {
      console.log('Missing:', { user: !!user, getValidAccessToken: !!getValidAccessToken });
      return;
    }

    console.log('🔧 Fetching competitions for user:', user.email, 'showHidden:', showHiddenSheets, 'isAdmin:', isUserAdmin());
    
    setIsLoading(true);
    setError(null);
    
    try {
      const token = await getValidAccessToken();
      console.log('Using token:', token ? 'present' : 'missing');
      
      // 🔧 TOUJOURS récupérer d'abord le nombre total d'onglets cachés (pour les admins)
      let hiddenCountData = null;
      if (isUserAdmin()) {
        try {
          console.log('🔧 Admin user - fetching hidden sheets count...');
          hiddenCountData = await sheetService.getSheetsList(
            config.GOOGLE_SHEET_ID, 
            getValidAccessToken,
            true // Inclure les onglets cachés pour compter
          );
          console.log('🔧 Hidden count data:', hiddenCountData);
          setHiddenSheetsCount(hiddenCountData.hiddenCount || 0);
        } catch (hiddenError) {
          console.error('🔧 Error fetching hidden count:', hiddenError);
          setHiddenSheetsCount(0);
        }
      }
      
      // 🆕 Ensuite récupérer la liste selon la préférence de l'utilisateur
      const data = await sheetService.getSheetsList(
        config.GOOGLE_SHEET_ID, 
        getValidAccessToken,
        isUserAdmin() && showHiddenSheets // Inclure les onglets cachés selon la préférence
      );
      
      console.log('🔧 Sheets data received:', {
        totalSheets: data.sheets?.length || 0,
        hiddenCount: data.hiddenCount || 0,
        includeHidden: data.includeHidden || false
      });
      
      const filteredSheets = data.sheets.filter(
        sheet => sheet.properties.title !== 'Authorizations'
      );
      
      // 🔧 Mettre à jour le compteur d'onglets cachés si pas déjà fait
      if (!hiddenCountData && data.hiddenCount !== undefined) {
        setHiddenSheetsCount(data.hiddenCount || 0);
      }
      
      const comps = await Promise.all(
        filteredSheets.map(async sheet => {
          const sheetName = sheet.properties.title;
          const isHidden = sheet.properties.hidden || false;
          
          const riders = await sheetService.getRiders(
            config.GOOGLE_SHEET_ID, 
            sheetName, 
            getValidAccessToken
          );
          
          return {
            name: sheetName,
            ridersCount: riders.length,
            hidden: isHidden // 🆕 Inclure le statut caché
          };
        })
      );
      
      console.log('🔧 Final competitions:', comps.map(c => ({ name: c.name, hidden: c.hidden })));
      
      setCompetitions(comps);
      await AsyncStorage.setItem('competitions', JSON.stringify(comps));
    } catch (e) {
      console.error('Error fetching competitions:', e);
      setError('Failed to fetch competitions');
      
      try {
        const cachedComps = await AsyncStorage.getItem('competitions');
        if (cachedComps) {
          const parsed = JSON.parse(cachedComps);
          setCompetitions(parsed);
          console.log('🔧 Loaded cached competitions:', parsed.length);
        }
      } catch (cacheError) {
        console.error('Failed to load cached competitions:', cacheError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 🆕 Fonction pour basculer l'affichage des onglets cachés (admin seulement)
  const toggleShowHiddenSheets = async () => {
    if (!isUserAdmin()) {
      console.warn('toggleShowHiddenSheets called by non-admin user');
      return false;
    }

    try {
      const newValue = !showHiddenSheets;
      console.log('🔧 Toggling showHiddenSheets:', showHiddenSheets, '->', newValue);
      
      const success = await AsyncStorageHelper.setItem('showHiddenSheets', newValue);
      
      if (success) {
        setShowHiddenSheets(newValue);
        console.log('Show hidden sheets preference updated:', newValue);
        
        // 🔧 Forcer la mise à jour immédiate des competitions
        setTimeout(() => {
          fetchCompetitions();
        }, 100);
        
        return true;
      } else {
        setError('Failed to save hidden sheets preference');
        return false;
      }
    } catch (e) {
      console.error('Failed to toggle show hidden sheets:', e);
      setError('Failed to save hidden sheets preference');
      return false;
    }
  };

  // 🔧 Fonction pour rafraîchir le compteur d'onglets cachés (debug)
  const refreshHiddenSheetsCount = async () => {
    if (!isUserAdmin()) return;
    
    try {
      console.log('🔧 Refreshing hidden sheets count...');
      const data = await sheetService.getSheetsList(
        config.GOOGLE_SHEET_ID, 
        getValidAccessToken,
        true // Toujours inclure les cachés pour compter
      );
      
      console.log('🔧 Refreshed hidden count:', data.hiddenCount);
      setHiddenSheetsCount(data.hiddenCount || 0);
      return data.hiddenCount || 0;
    } catch (error) {
      console.error('🔧 Error refreshing hidden count:', error);
      return 0;
    }
  };

  const saveRefreshInterval = async (interval) => {
    try {
      // Validation d'entrée
      const numInterval = parseInt(interval, 10);
      if (isNaN(numInterval) || numInterval < 0) {
        console.error('Invalid refresh interval:', interval);
        setError('Invalid refresh interval');
        return false;
      }
      
      const success = await AsyncStorageHelper.setItem('refreshInterval', numInterval);
      if (success) {
        setRefreshInterval(numInterval);
        return true;
      } else {
        setError('Failed to save refresh interval');
        return false;
      }
    } catch (e) {
      console.error('Failed to save refresh interval:', e);
      setError('Failed to save refresh interval');
      return false;
    }
  };

  const testConnection = async (id = config.GOOGLE_SHEET_ID) => {
    if (!user || !getValidAccessToken) {
      setError('You must be logged in to test connection');
      return false;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      await sheetService.getSheetsList(id, getValidAccessToken, false); // Test sans onglets cachés
      return true;
    } catch (e) {
      console.error('Connection test failed:', e);
      setError('Failed to connect to the Google Sheet');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SheetContext.Provider
      value={{
        sheetId: config.GOOGLE_SHEET_ID,
        competitions,
        isLoading,
        error,
        refreshInterval,
        saveRefreshInterval,
        fetchCompetitions,
        testConnection,
        // 🆕 Nouvelles propriétés pour les onglets cachés
        showHiddenSheets,
        hiddenSheetsCount,
        toggleShowHiddenSheets,
        refreshHiddenSheetsCount, // 🔧 Fonction de debug
        canManageHiddenSheets: isUserAdmin() // Helper pour vérifier les permissions
      }}
    >
      {children}
    </SheetContext.Provider>
  );
};